import React, { useState, useEffect } from 'react';
import { Settings, Eye, Type, Contrast, Minus, Plus, Keyboard, RotateCcw } from 'lucide-react';

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [fontSize, setFontSize] = useState(16);
  const [contrast, setContrast] = useState('normal');
  const [lineHeight, setLineHeight] = useState(1.5);
  const [keyboardNavigation, setKeyboardNavigation] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [textSpacing, setTextSpacing] = useState('normal');

  useEffect(() => {
    document.documentElement.style.fontSize = `${fontSize}px`;
    document.documentElement.style.lineHeight = lineHeight;
    
    // Remove existing accessibility classes
    document.documentElement.className = document.documentElement.className
      .replace(/contrast-\w+|keyboard-nav|reduced-motion|text-spacing-\w+/g, '');
    
    // Apply contrast
    if (contrast !== 'normal') {
      document.documentElement.classList.add(`contrast-${contrast}`);
    }
    
    // Apply keyboard navigation styles
    if (keyboardNavigation) {
      document.documentElement.classList.add('keyboard-nav');
    }
    
    // Apply reduced motion
    if (reducedMotion) {
      document.documentElement.classList.add('reduced-motion');
    }
    
    // Apply text spacing
    if (textSpacing !== 'normal') {
      document.documentElement.classList.add(`text-spacing-${textSpacing}`);
    }
  }, [fontSize, contrast, lineHeight, keyboardNavigation, reducedMotion, textSpacing]);

  const increaseFontSize = () => {
    if (fontSize < 24) setFontSize(fontSize + 2);
  };

  const decreaseFontSize = () => {
    if (fontSize > 12) setFontSize(fontSize - 2);
  };

  const toggleContrast = () => {
    setContrast(contrast === 'normal' ? 'high' : 'normal');
  };

  const increaseLineHeight = () => {
    if (lineHeight < 2.5) setLineHeight(lineHeight + 0.25);
  };

  const decreaseLineHeight = () => {
    if (lineHeight > 1) setLineHeight(lineHeight - 0.25);
  };

  const toggleKeyboardNav = () => {
    setKeyboardNavigation(!keyboardNavigation);
  };

  const toggleReducedMotion = () => {
    setReducedMotion(!reducedMotion);
  };

  const toggleTextSpacing = () => {
    setTextSpacing(textSpacing === 'normal' ? 'wide' : 'normal');
  };

  const resetAll = () => {
    setFontSize(16);
    setContrast('normal');
    setLineHeight(1.5);
    setKeyboardNavigation(false);
    setReducedMotion(false);
    setTextSpacing('normal');
  };

  return (
    <>
      <style jsx global>{`
        .contrast-high {
          filter: contrast(150%) brightness(1.2);
        }
        .contrast-high * {
          border-color: #000 !important;
          text-shadow: 1px 1px 1px rgba(0,0,0,0.5) !important;
        }
        
        /* Keyboard navigation styles */
        .keyboard-nav *:focus {
          outline: 3px solid #007cba !important;
          outline-offset: 2px !important;
          box-shadow: 0 0 0 3px rgba(0, 124, 186, 0.3) !important;
        }
        
        /* Reduced motion */
        .reduced-motion * {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
          scroll-behavior: auto !important;
        }
        
        /* Text spacing */
        .text-spacing-wide * {
          letter-spacing: 0.12em !important;
          word-spacing: 0.16em !important;
        }
        
        /* Skip link */
        .skip-link {
          position: fixed;
          top: -50px;
          right: 20px;
          background: #007cba;
          color: white;
          padding: 12px 16px;
          text-decoration: none;
          z-index: 10001;
          border-radius: 6px;
          font-weight: bold;
          font-size: 16px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          border: 2px solid white;
          direction: rtl;
          transition: top 0.2s ease;
        }
        .skip-link:focus {
          top: 20px;
        }
        .skip-link:hover:focus {
          background: #005a87;
        }
        .accessibility-widget {
          position: fixed;
          bottom: 20px;
          right: 20px;
          z-index: 9999;
          direction: ltr;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
        }
        .accessibility-panel {
          background: white;
          border: 2px solid #333;
          border-radius: 8px;
          padding: 16px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          margin-bottom: 8px;
          min-width: 280px;
          position: relative;
          right: 0;
        }
        .accessibility-button {
          background: #007cba;
          color: white;
          border: none;
          border-radius: 50%;
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          transition: all 0.3s ease;
        }
        .accessibility-button:hover {
          background: #005a87;
          transform: scale(1.1);
        }
        .control-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
          gap: 8px;
        }
        .control-button {
          background: #f0f0f0;
          border: 1px solid #ccc;
          border-radius: 4px;
          padding: 4px 8px;
          cursor: pointer;
          font-size: 14px;
        }
        .control-button:hover {
          background: #e0e0e0;
        }
        .control-button.active {
          background: #007cba;
          color: white;
        }
        .size-display {
          font-weight: bold;
          min-width: 40px;
          text-align: center;
        }
      `}</style>
      
      <div className="accessibility-widget">
        {isOpen && (
          <div className="accessibility-panel" role="dialog" aria-labelledby="accessibility-title">
            <h3 id="accessibility-title" style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 'bold' }}>
              אפשרויות נגישות
            </h3>
            
            <div className="control-row">
              <span>גודל טקסט:</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button className="control-button" onClick={decreaseFontSize}>
                  <Minus size={16} />
                </button>
                <span className="size-display">{fontSize}px</span>
                <button className="control-button" onClick={increaseFontSize}>
                  <Plus size={16} />
                </button>
              </div>
            </div>

            <div className="control-row">
              <span>ניגודיות:</span>
              <button 
                className={`control-button ${contrast === 'high' ? 'active' : ''}`}
                onClick={toggleContrast}
              >
                <Contrast size={16} style={{ marginRight: '4px' }} />
                {contrast === 'high' ? 'גבוהה' : 'רגילה'}
              </button>
            </div>

            <div className="control-row">
              <span>מרווח שורות:</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button className="control-button" onClick={decreaseLineHeight}>
                  <Minus size={16} />
                </button>
                <span className="size-display">{lineHeight.toFixed(1)}</span>
                <button className="control-button" onClick={increaseLineHeight}>
                  <Plus size={16} />
                </button>
              </div>
            </div>

            <div className="control-row">
              <span>ניווט מקלדת:</span>
              <button 
                className={`control-button ${keyboardNavigation ? 'active' : ''}`}
                onClick={toggleKeyboardNav}
              >
                <Keyboard size={16} style={{ marginRight: '4px' }} />
                {keyboardNavigation ? 'פעיל' : 'כבוי'}
              </button>
            </div>

            <div className="control-row">
              <span>מרווח טקסט:</span>
              <button 
                className={`control-button ${textSpacing === 'wide' ? 'active' : ''}`}
                onClick={toggleTextSpacing}
              >
                <Type size={16} style={{ marginRight: '4px' }} />
                {textSpacing === 'wide' ? 'רחב' : 'רגיל'}
              </button>
            </div>

            <div className="control-row">
              <span>הפחתת תנועה:</span>
              <button 
                className={`control-button ${reducedMotion ? 'active' : ''}`}
                onClick={toggleReducedMotion}
              >
                <Eye size={16} style={{ marginRight: '4px' }} />
                {reducedMotion ? 'פעיל' : 'כבוי'}
              </button>
            </div>

            <button 
              onClick={resetAll}
              style={{
                width: '100%',
                padding: '8px',
                background: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginTop: '8px'
              }}
            >
              איפוס הגדרות
            </button>
          </div>
        )}
        
        <button 
          className="accessibility-button"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? "סגור תפריט נגישות" : "פתח תפריט נגישות"}
          aria-expanded={isOpen}
          title="אפשרויות נגישות"
        >
          <Settings size={24} />
        </button>
      </div>
    </>
  );
};

export default AccessibilityWidget;